import { useMemo, useState, type ReactNode } from 'react';
import { useMetrics, useUsers } from '@/hooks/queries';
import { useAuth } from '@/hooks/useAuth';
import { CardSkeleton, ErrorState } from '@/components/ui';
import { SeriesChart, type ChartSeries } from '@/components/metrics/SeriesChart';
import { METRIC_TOKENS, MetricsLedger } from '@/components/metrics/MetricsLedger';
import { MembersTable, TopClients, TopDepositors } from '@/components/metrics/MetricsTables';
import { formatDate, MESES, PERIOD_LABELS, todayISO } from '@/lib/format';
import type { Metrics, MetricsPoint, PeriodKey } from '@/lib/types';
import '@/components/metrics/metrics.css';

const PERIODS: PeriodKey[] = ['month', 'lastMonth', 'week', 'quarter', 'year', 'all'];

/** O período como se lê: o personalizado vira as duas datas. */
function periodText(period: Metrics['period']): string {
  if (period.key !== 'custom') return period.label;
  const from = period.from ? formatDate(period.from) : null;
  const to = period.to ? formatDate(period.to) : null;
  if (from && to) return `${from} a ${to}`;
  if (from) return `desde ${from}`;
  return to ? `até ${to}` : period.label;
}

function pointTitle(point: MetricsPoint, granularity: 'day' | 'month'): string {
  if (granularity === 'day') return formatDate(point.key);
  const [year, month] = point.key.split('-');
  return `${MESES[Number(month) - 1] ?? ''} de ${year}`;
}

/**
 * Métricas de vendas e de depósitos na corretora. O administrador vê a
 * operação inteira (ou filtra por membro); o membro vê só os próprios números.
 */
export function MetricsPage() {
  const { isAdmin } = useAuth();
  const [period, setPeriod] = useState<PeriodKey>('month');
  const [from, setFrom] = useState(() => `${todayISO().slice(0, 8)}01`);
  const [to, setTo] = useState(todayISO);
  const [memberId, setMemberId] = useState('');
  const custom = period === 'custom';

  const { data: members } = useUsers({}, { enabled: isAdmin });
  const { data, isLoading, isPlaceholderData, error } = useMetrics({
    ...(custom ? { from: from || undefined, to: to || undefined } : { period }),
    memberId: memberId || undefined,
  });

  return (
    <>
      <header className="metrics-header">
        <div>
          <h1 className="page-title">Métricas</h1>
          <p className="page-lede">
            Quanto se vendeu no período, comparado com o anterior, e quanto os clientes têm hoje na corretora.
          </p>
        </div>

        <div className="toolbar metrics-filters">
          <select className="inp" value={period} onChange={(event) => setPeriod(event.target.value as PeriodKey)} aria-label="Período">
            {PERIODS.map((option) => (
              <option key={option} value={option}>{PERIOD_LABELS[option]}</option>
            ))}
            <option value="custom">Personalizado</option>
          </select>

          {custom ? (
            <>
              <input className="inp metrics-date" type="date" value={from} onChange={(event) => setFrom(event.target.value)} aria-label="Data inicial" />
              <span className="muted" style={{ fontSize: 12 }}>até</span>
              <input className="inp metrics-date" type="date" value={to} onChange={(event) => setTo(event.target.value)} aria-label="Data final" />
            </>
          ) : null}

          {isAdmin ? (
            <select className="inp" value={memberId} onChange={(event) => setMemberId(event.target.value)} aria-label="Membro">
              <option value="">Toda a operação</option>
              {members?.map((member) => (
                <option key={member.id} value={member.id}>{member.name}</option>
              ))}
            </select>
          ) : null}
        </div>
      </header>

      {error ? (
        <ErrorState message={(error as Error).message} />
      ) : isLoading || !data ? (
        <div className="metrics-body metrics-stack">
          <CardSkeleton height={250} />
          <CardSkeleton height={300} />
        </div>
      ) : (
        <MetricsBody data={data} stale={isPlaceholderData} isAdmin={isAdmin} onPeriod={setPeriod} />
      )}
    </>
  );
}

interface MetricsBodyProps {
  data: Metrics;
  /** Os números ainda são do filtro anterior — ficam esmaecidos até os novos chegarem. */
  stale: boolean;
  isAdmin: boolean;
  onPeriod: (period: PeriodKey) => void;
}

function MetricsBody({ data, stale, isAdmin, onPeriod }: MetricsBodyProps) {
  const { series, scope } = data;
  const personal = Boolean(scope.memberId);
  const hasSales = data.overall.receiptCount > 0;
  const { granularity } = series;
  const when = periodText(data.period);
  const who = personal && isAdmin ? ` por ${scope.memberName}` : '';

  const labels = useMemo(() => series.points.map((point) => point.label), [series]);
  const titles = useMemo(() => series.points.map((point) => pointTitle(point, granularity)), [series, granularity]);
  const chartSeries = useMemo<ChartSeries[]>(
    () => [
      {
        key: 'sold',
        label: 'Vendido',
        token: METRIC_TOKENS.sold,
        mark: 'bar',
        values: series.points.map((point) => point.salesSold + point.leverageSold),
      },
    ],
    [series],
  );

  return (
    <div className={`metrics-body metrics-stack${stale ? ' is-stale' : ''}`} aria-busy={stale}>
      <MetricsLedger data={data} when={when} who={who} onPeriod={onPeriod} />

      {hasSales ? (
        <Block title={granularity === 'day' ? 'Vendido por dia' : 'Vendido por mês'} note={when}>
          <div className="card">
            <SeriesChart
              labels={labels}
              titles={titles}
              series={chartSeries}
              stackTotalLabel={granularity === 'day' ? 'Vendido no dia' : 'Vendido no mês'}
              ariaLabel={`Vendido ${granularity === 'day' ? 'dia a dia' : 'mês a mês'} — ${when}`}
            />
          </div>
        </Block>
      ) : null}

      {!personal && data.members.length > 0 ? (
        <Block title="Por membro" note={`vendido em ${when.toLowerCase()} · corretora hoje`}>
          <MembersTable members={data.members} />
        </Block>
      ) : null}

      <div className="metrics-rankings">
        <Block title="Clientes que mais compraram" note={when}>
          <div className="card">
            <TopClients clients={data.topClients} showOwner={!personal} />
          </div>
        </Block>
        <Block title="Maiores depósitos" note="posição de hoje">
          <div className="card">
            <TopDepositors clients={data.topDepositors} showOwner={!personal} />
          </div>
        </Block>
      </div>
    </div>
  );
}

/** Uma seção da página: título curto, a nota do recorte ao lado e o conteúdo. */
function Block({ title, note, children }: { title: string; note: string; children: ReactNode }) {
  return (
    <section className="metrics-block" aria-label={title}>
      <div className="metrics-block-head">
        <h2 className="section-title">{title}</h2>
        <span className="metrics-block-note">{note}</span>
        <span className="section-rule" />
      </div>
      {children}
    </section>
  );
}
